import React from 'react';
import { Link } from 'react-router-dom';

const Services: React.FC = () => {
  return (
    <>
      {/* Page Header */}
      <section className="bg-dark text-white py-5 mt-5">
        <div className="container py-4">
          <div className="row">
            <div className="col-lg-8">
              <h1 className="display-5 fw-bold mb-3">Our Services</h1>
              <p className="lead mb-0">Building assessments that make facilities more sustainable and less expensive to run — plus the design review and commissioning support to keep them that way.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Introduction */}
      <section className="section section-light">
        <div className="container">
          <div className="row g-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right">
              <img 
                src="https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=800&q=80" 
                alt="Consulting for sustainable practices" 
                className="img-fluid rounded-4 shadow-lg" 
              />
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <h2 className="mb-4">Building <span className="text-primary">Assessment</span> Services</h2>
              <p className="lead mb-4">At Sustainability Pros, we assess how buildings actually perform — and give owners a clear, prioritized path to lower energy use, lower operating costs, and a more sustainable facility.</p>
              <p className="mb-4">Whether you're managing an aging portfolio, planning capital improvements, or chasing energy and sustainability goals, our team brings hands-on MEP field expertise and a thorough, owner-focused process — grounded in real equipment data, not assumptions.</p>
              <p className="mb-0">We work as an extension of the owner's team, and we stay involved through design review and commissioning support when a project calls for it — so the performance we assess is the performance you actually get.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="section bg-eco-pattern">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Our <span className="text-primary">Service</span> Offerings</h2>
              <p className="lead">Assessment-led consulting for owners and facility managers — with design review and commissioning support available across a building's lifecycle.</p>
            </div>
          </div>
          
          {/* Existing Building Assessment (lead service) */}
          <div className="row g-5 mb-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right">
              <img
                src="/images/ahu-interior-inspection.jpeg"
                alt="Interior inspection of an air handling unit during a facility assessment"
                className="img-fluid rounded shadow-lg"
              />
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <div className="service-content">
                <h3 className="text-primary mb-4">Existing Building Assessment</h3>
                <p className="lead mb-3">Our core service: a comprehensive on-site evaluation of your mechanical, electrical, and plumbing systems — giving you a clear, prioritized picture of what your building needs to run efficiently, sustainably, and reliably.</p>
                <ul className="list-unstyled mb-4">
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    HVAC equipment condition evaluation (boilers, pumps, air handlers, compressors)
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Building Automation System (BAS) assessment
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Energy-use and efficiency observations tied to operating cost
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Prioritized action items with repair/replacement recommendations
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Capital planning and sustainability roadmap documentation
                  </li>
                </ul>
                <Link to="/contact" className="btn btn-primary">
                  Request Consultation <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Design Document Review */}
          <div className="row g-5 mb-5 align-items-center flex-lg-row-reverse">
            <div className="col-lg-6" data-aos="fade-left">
              <img
                src="https://images.unsplash.com/photo-1542744173-8e7e53415bb0?auto=format&fit=crop&w=800&q=80"
                alt="Reviewing construction drawings"
                className="img-fluid rounded shadow-lg"
              />
            </div>
            <div className="col-lg-6" data-aos="fade-right">
              <div className="service-content">
                <h3 className="text-primary mb-4">Design Document Review</h3>
                <p className="lead mb-3">Independent review of construction documents to verify that mechanical, electrical, and plumbing systems are designed correctly and completely before work begins.</p>
                <ul className="list-unstyled mb-4">
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Mechanical, electrical, and plumbing drawing review
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Specifications and sequences of operation review
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Design-phase coordination with engineering team
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Constructability and completeness evaluation
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Issue tracking and resolution documentation
                  </li>
                </ul>
                <Link to="/contact" className="btn btn-primary">
                  Request Consultation <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Construction Oversight */}
          <div className="row g-5 mb-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right">
              <img
                src="https://images.unsplash.com/photo-1504917595217-d4dc5ebe6122?auto=format&fit=crop&w=800&q=80"
                alt="Construction site oversight"
                className="img-fluid rounded shadow-lg"
              />
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <div className="service-content">
                <h3 className="text-primary mb-4">Construction Phase Oversight</h3>
                <p className="lead mb-3">On-site observation and documentation during construction to confirm that installations align with approved design documents and owner requirements.</p>
                <ul className="list-unstyled mb-4">
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Scheduled and unannounced site visits
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Installation verification against approved drawings
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Deficiency identification and tracking
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Contractor coordination and issue resolution
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Construction observation reports
                  </li>
                </ul>
                <Link to="/contact" className="btn btn-primary">
                  Request Consultation <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Energy & Sustainability Analysis */}
          <div className="row g-5 mb-5 align-items-center flex-lg-row-reverse">
            <div className="col-lg-6" data-aos="fade-left">
              <img
                src="/images/boiler-exhaust-stack.jpeg"
                alt="Boiler and exhaust stack evaluated during an energy analysis"
                className="img-fluid rounded shadow-lg"
              />
            </div>
            <div className="col-lg-6" data-aos="fade-right">
              <div className="service-content">
                <h3 className="text-primary mb-4">Energy &amp; Sustainability Analysis</h3>
                <p className="lead mb-3">We quantify where your building wastes energy and money — modeling conservation measures and payback so you can invest in the upgrades that deliver the biggest return.</p>
                <ul className="list-unstyled mb-4">
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Boiler and equipment energy conservation measure (ECM) analysis
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Fuel-savings and simple-payback / ROI modeling
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Efficiency observations tied to real operating costs
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Support for sustainability goals and utility incentives
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Capital planning prioritized by cost and carbon impact
                  </li>
                </ul>
                <Link to="/contact" className="btn btn-primary">
                  Request Consultation <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Commissioning Support & Closeout */}
          <div className="row g-5 mb-5 align-items-center">
            <div className="col-lg-6" data-aos="fade-right">
              <img
                src="https://images.unsplash.com/photo-1573497491765-55d568e4288e?auto=format&fit=crop&w=800&q=80"
                alt="Facility staff training session"
                className="img-fluid rounded shadow-lg"
              />
            </div>
            <div className="col-lg-6" data-aos="fade-left">
              <div className="service-content">
                <h3 className="text-primary mb-4">Commissioning Support &amp; Closeout</h3>
                <p className="lead mb-3">When a project calls for commissioning — often to satisfy LEED or code requirements — we provide owner's-side commissioning support and a clean closeout handoff, so the systems you paid for actually perform.</p>
                <ul className="list-unstyled mb-4">
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Owner's Project Requirements (OPR) input and design review
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Functional performance test observation and deficiency tracking
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Test-and-balance and commissioning script review
                  </li>
                  <li className="mb-2">
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    Facility staff operational training coordination
                  </li>
                  <li>
                    <i className="bi bi-check-circle-fill text-primary me-2"></i>
                    O&M manual and as-built collection for a working handoff
                  </li>
                </ul>
                <Link to="/contact" className="btn btn-primary">
                  Request Consultation <i className="bi bi-arrow-right ms-2"></i>
                </Link>
              </div>
            </div>
          </div>

          {/* Digital Building Handoff */}
          <div className="row g-5 align-items-center flex-lg-row-reverse">
            <div className="col-lg-6" data-aos="fade-left">
              <div className="bg-dark rounded-4 p-4 p-md-5 text-white h-100">
                <h5 className="text-primary mb-4"><i className="bi bi-arrow-left-right me-2"></i>Binder in a Drawer vs. Day-One Operations</h5>
                <ul className="list-unstyled mb-0">
                  <li className="mb-3 d-flex">
                    <i className="bi bi-hdd-stack text-primary me-3 mt-1" style={{ fontSize: '1.3rem' }}></i>
                    <span>Every asset in a structured inventory — make, model, serial number, location</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-journal-text text-primary me-3 mt-1" style={{ fontSize: '1.3rem' }}></i>
                    <span>O&amp;M manuals, warranties, and sequences of operation linked to each asset — not buried in a PDF</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-calendar-check text-primary me-3 mt-1" style={{ fontSize: '1.3rem' }}></i>
                    <span>Preventive maintenance schedule built and running from day one of occupancy</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-arrow-repeat text-primary me-3 mt-1" style={{ fontSize: '1.3rem' }}></i>
                    <span>Data that stays current as the building changes — tenant moves, replacements, upgrades</span>
                  </li>
                </ul>
              </div>
            </div>
            <div className="col-lg-6" data-aos="fade-right">
              <div className="service-content">
                <h3 className="text-primary mb-4">Digital Building Handoff</h3>
                <p className="lead mb-3">The traditional closeout deliverable is a binder or a thumb drive — and it starts going stale the day it's handed over. We deliver the building's information the way it will actually be used: as a live, structured operational system.</p>
                <p className="mb-4">For new construction, we transfer closeout data from the BIM model (COBie) directly into MEP Tools. For existing buildings, our assessments rebuild the documentation that was never handed over in the first place. Either way, your facility team starts with a working system, not a stack of documents to decipher.</p>
                <div className="d-flex flex-wrap gap-2">
                  <Link to="/contact" className="btn btn-primary">
                    Request Consultation <i className="bi bi-arrow-right ms-2"></i>
                  </Link>
                  <Link to="/mep-tools#digital-building-handoff" className="btn btn-outline-primary">
                    How It Works
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Additional <span className="text-primary">Services</span></h2>
              <p className="lead">Specialized support services to complement our core assessment work.</p>
            </div>
          </div>
          
          <div className="row g-4">
            <div className="col-md-6 col-lg-4" data-aos="fade-up">
              <div className="card h-100 p-4">
                <div className="feature-icon mb-3">
                  <i className="bi bi-clipboard-data-fill"></i>
                </div>
                <h4>Equipment Condition Reporting</h4>
                <p>Detailed field reports documenting the condition of individual building systems and equipment, with action items prioritized by urgency and impact.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Field inspection and documentation</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Prioritized repair and replacement recommendations</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Coordination with maintenance contractors</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100 p-4">
                <div className="feature-icon mb-3">
                  <i className="bi bi-cpu-fill"></i>
                </div>
                <h4>BAS & Controls Evaluation</h4>
                <p>Assessment of building automation systems and controls sequences to identify gaps between intended and actual operation.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>BAS/JACE system review</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Sequence of operations verification</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Controls deficiency documentation</span>
                  </li>
                </ul>
              </div>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100 p-4">
                <div className="feature-icon mb-3">
                  <i className="bi bi-people-fill"></i>
                </div>
                <h4>Facility Staff Training</h4>
                <p>Hands-on training for operations and maintenance staff on newly installed or upgraded building systems.</p>
                <ul className="list-unstyled mt-3 mb-0">
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>System-specific operational training</span>
                  </li>
                  <li className="mb-2 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>Preventive maintenance guidance</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span>O&M documentation review and handoff</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Highway to Sustainability Section */}
      <section className="section bg-dark text-white">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">The <span className="text-primary">Highway to Sustainability</span></h2>
              <p className="lead">Our comprehensive 5-phase approach to ensure sustainable outcomes for your project.</p>
            </div>
          </div>
          
          <div className="row g-4">
            {/* Phase 1 */}
            <div className="col-lg-4" data-aos="fade-up">
              <div className="card h-100 bg-dark text-white border-primary p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                    1
                  </div>
                  <h3 className="card-title mb-0">Concept & Design</h3>
                </div>
                <ul className="list-unstyled mb-0">
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Owner Project Requirements (OPR)</strong>: Creating a comprehensive document that fully describes your intent and requirements.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Basis of Design (BOD)</strong>: Engineering firms generate documents describing their understanding and design parameters.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>BOD Review</strong>: Ensuring the design meets all requirements in the OPR.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Construction Documentation</strong>: Generating detailed construction drawings.</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Construction Documentation Review</strong>: Ensuring drawings are complete and accurate.</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Phase 2 */}
            <div className="col-lg-4" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100 bg-dark text-white border-primary p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                    2
                  </div>
                  <h3 className="card-title mb-0">Pre-Construction</h3>
                </div>
                <ul className="list-unstyled mb-0">
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Request for Qualification (RFQ)</strong>: Soliciting potential contractors to provide their qualifications and resources.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>RFQ Review</strong>: Selecting qualified contractors to submit construction proposals.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Request for Proposal (RFP)</strong>: Soliciting construction proposals from qualified contractors.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>RFP Review</strong>: Evaluating proposals to select the best contractor.</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Drawing Review</strong>: Final review of construction drawings before beginning work.</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Phase 3 */}
            <div className="col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100 bg-dark text-white border-primary p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                    3
                  </div>
                  <h3 className="card-title mb-0">Construction</h3>
                </div>
                <ul className="list-unstyled mb-0">
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Construction Meeting</strong>: Establishing responsibilities and communication protocols.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Project Scheduling</strong>: Setting clear timelines for all project phases.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>During Construction Testing</strong>: Regular testing during construction to ensure compliance with BOD.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Functional Performance Tests</strong>: Testing after construction completion.</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                    <span><strong>Close-Out Meeting</strong>: Reviewing construction issues, collecting documentation and operation manuals.</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Phase 4 */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="300">
              <div className="card h-100 bg-dark text-white border-primary p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                    4
                  </div>
                  <h3 className="card-title mb-0">Post-Construction</h3>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-md-0">
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Operational Training</strong>: Coordinating training for operational staff.</span>
                      </li>
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Maintenance Training</strong>: Ensuring staff receives proper system operational training.</span>
                      </li>
                      <li className="d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Utility Rate Review</strong>: Ensuring advantageous utility rate schedules.</span>
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Utility Monitoring</strong>: Providing methods to monitor consumption and costs.</span>
                      </li>
                      <li className="d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Continuous Commissioning</strong>: Remote monitoring of system operations.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Phase 5 */}
            <div className="col-lg-6" data-aos="fade-up" data-aos-delay="400">
              <div className="card h-100 bg-dark text-white border-primary p-4">
                <div className="d-flex align-items-center mb-4">
                  <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center me-3" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                    5
                  </div>
                  <h3 className="card-title mb-0">Occupancy & Operations</h3>
                </div>
                <div className="row">
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-md-0">
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Ongoing Utility Monitoring</strong>: Continuous tracking of consumption and costs.</span>
                      </li>
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Continuous Commissioning</strong>: Regular testing of systems and equipment.</span>
                      </li>
                      <li className="d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Sensor Calibration</strong>: Annual calibration of critical equipment sensors.</span>
                      </li>
                    </ul>
                  </div>
                  <div className="col-md-6">
                    <ul className="list-unstyled mb-0">
                      <li className="mb-3 d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Maintenance Training Updates</strong>: Continuing education for maintenance staff.</span>
                      </li>
                      <li className="d-flex">
                        <i className="bi bi-check-circle-fill text-primary me-2 mt-1"></i>
                        <span><strong>Space Operations & Cost</strong>: Tools to assess operations and calculate carbon footprint.</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <div className="row mt-5">
            <div className="col-lg-10 mx-auto">
              <div className="bg-primary bg-opacity-25 p-4 rounded-4 border border-primary" data-aos="fade-up" data-aos-delay="500">
                <h4 className="text-primary mb-3">Our Sustainability-Pros Service Highlights</h4>
                <ul className="list-unstyled mb-0">
                  <li className="mb-3 d-flex">
                    <i className="bi bi-star-fill text-primary me-2 mt-1"></i>
                    <span>The sustainability of a new facility depends on the owner fully describing their intents and requirements and the design engineer adhering to those intents and requirements.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-star-fill text-primary me-2 mt-1"></i>
                    <span>We assist owners with OPR documentation development and review of the BOD and design drawings. Errors or omissions in these documents will lead to sustainability issues.</span>
                  </li>
                  <li className="mb-3 d-flex">
                    <i className="bi bi-star-fill text-primary me-2 mt-1"></i>
                    <span>We provide thorough design drawing review to ensure accuracy and proper installation of materials, equipment, and systems.</span>
                  </li>
                  <li className="d-flex">
                    <i className="bi bi-star-fill text-primary me-2 mt-1"></i>
                    <span>We provide tools for comprehensive testing, including Duct Air Leakage Tests (DALT), Test, Adjust, and Balance Verification (TABV), Sequence-of-Operation Verification (S-o-OV), and Space Pressurization Verification (SPV).</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>
      
      {/* Our Process */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Our Project <span className="text-primary">Workflow</span></h2>
              <p className="lead">How we approach an assessment engagement from start to finish.</p>
            </div>
          </div>
          
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="row g-4">
                <div className="col-md-6" data-aos="fade-right">
                  <div className="d-flex">
                    <div className="me-4">
                      <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                        1
                      </div>
                    </div>
                    <div>
                      <h4 className="text-primary">Scoping & Kickoff</h4>
                      <p>We discuss your building, understand your goals and problem areas, and develop an assessment plan tailored to your needs, timeline, and budget.</p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6" data-aos="fade-left">
                  <div className="d-flex">
                    <div className="me-4">
                      <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                        2
                      </div>
                    </div>
                    <div>
                      <h4 className="text-primary">Document Review</h4>
                      <p>We review design documents, drawings, specifications, and sequences of operation — identifying issues and coordinating with the design team before construction begins.</p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6" data-aos="fade-right" data-aos-delay="100">
                  <div className="d-flex">
                    <div className="me-4">
                      <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                        3
                      </div>
                    </div>
                    <div>
                      <h4 className="text-primary">Field Assessment</h4>
                      <p>We conduct on-site inspection and photographic documentation of your MEP equipment — coordinating with service and BAS contractors and running functional tests where warranted.</p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6" data-aos="fade-left" data-aos-delay="100">
                  <div className="d-flex">
                    <div className="me-4">
                      <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                        4
                      </div>
                    </div>
                    <div>
                      <h4 className="text-primary">Analysis & Energy Modeling</h4>
                      <p>We score asset condition, model energy conservation measures, and calculate the savings and payback behind each opportunity we identify.</p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6" data-aos="fade-right" data-aos-delay="200">
                  <div className="d-flex">
                    <div className="me-4">
                      <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                        5
                      </div>
                    </div>
                    <div>
                      <h4 className="text-primary">Prioritized Report</h4>
                      <p>We deliver a written report with photographic evidence and prioritized recommendations — separating immediate fixes from near-term repairs and capital planning items, each ranked by cost, reliability, and sustainability impact.</p>
                    </div>
                  </div>
                </div>
                
                <div className="col-md-6" data-aos="fade-left" data-aos-delay="200">
                  <div className="d-flex">
                    <div className="me-4">
                      <div className="bg-primary rounded-circle text-white d-flex align-items-center justify-content-center" style={{ width: '50px', height: '50px', fontSize: '1.5rem' }}>
                        6
                      </div>
                    </div>
                    <div>
                      <h4 className="text-primary">Post-Occupancy Follow-Up</h4>
                      <p>For clients who want continued support, we can return after occupancy to verify systems are performing as expected and help resolve any issues that emerge during the first season of operation.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="section section-light">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Every Project is <span className="text-primary">Custom Scoped</span></h2>
              <p className="lead">Our fees are based on the scope, complexity, and duration of each engagement. Contact us to discuss your project and we'll provide a tailored proposal.</p>
            </div>
          </div>
          
          <div className="row g-4 justify-content-center">
            <div className="col-md-6 col-lg-4" data-aos="fade-up">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-building"></i>
                </div>
                <h4>Energy &amp; Sustainability Analysis</h4>
                <p>Scoped by the systems analyzed and the depth of energy modeling required. Quantifies conservation measures, savings, and payback for capital planning.</p>
                <Link to="/contact" className="btn btn-outline-primary mt-auto">
                  Request a Proposal
                </Link>
              </div>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="100">
              <div className="card h-100 p-4 text-center border border-primary border-2 shadow">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-search"></i>
                </div>
                <h4>Existing Building Assessment</h4>
                <p>Scoped by number of buildings, systems to be assessed, and depth of reporting required. Single-visit and multi-building engagements available.</p>
                <Link to="/contact" className="btn btn-primary mt-auto">
                  Request a Proposal
                </Link>
              </div>
            </div>

            <div className="col-md-6 col-lg-4" data-aos="fade-up" data-aos-delay="200">
              <div className="card h-100 p-4 text-center">
                <div className="feature-icon mx-auto mb-3">
                  <i className="bi bi-file-earmark-text"></i>
                </div>
                <h4>Design Document Review</h4>
                <p>Scoped by number of document packages and review cycles. Can be a standalone engagement or part of a broader assessment scope.</p>
                <Link to="/contact" className="btn btn-outline-primary mt-auto">
                  Request a Proposal
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="section bg-eco-pattern">
        <div className="container">
          <div className="row text-center mb-5">
            <div className="col-lg-8 mx-auto">
              <h2 className="mb-3">Frequently Asked <span className="text-primary">Questions</span></h2>
              <p className="lead">Common questions about building assessments and how we work.</p>
            </div>
          </div>
          
          <div className="row">
            <div className="col-lg-10 mx-auto">
              <div className="accordion" id="servicesAccordion">
                <div className="accordion-item border-0 mb-4 shadow-sm" data-aos="fade-up">
                  <h3 className="accordion-header">
                    <button className="accordion-button" type="button" data-bs-toggle="collapse" data-bs-target="#faq1">
                      What is a building assessment and why does it matter?
                    </button>
                  </h3>
                  <div id="faq1" className="accordion-collapse collapse show" data-bs-parent="#servicesAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">A building assessment is a structured, on-site evaluation of your mechanical, electrical, and plumbing systems — documenting their condition, how they're performing, and where they're wasting energy. Most facilities carry hidden inefficiencies and deferred maintenance that quietly drive up utility bills and shorten equipment life. An assessment surfaces those issues and turns them into a prioritized plan, so you spend capital where it delivers the biggest savings and sustainability gains.</p>
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 mb-4 shadow-sm" data-aos="fade-up" data-aos-delay="100">
                  <h3 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq2">
                      When is the right time to have our building assessed?
                    </button>
                  </h3>
                  <div id="faq2" className="accordion-collapse collapse" data-bs-parent="#servicesAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">Any time — but a few moments pay off especially well: before building a capital plan or budget, when energy costs are climbing, when equipment is nearing the end of its service life, or when you're pursuing sustainability or certification goals. An assessment gives you the data to make those decisions with confidence instead of guesswork. For buildings under design or construction, we can also review documents early, when issues are still cheap to fix on paper.</p>
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 mb-4 shadow-sm" data-aos="fade-up" data-aos-delay="200">
                  <h3 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq3">
                      Do you provide commissioning services too?
                    </button>
                  </h3>
                  <div id="faq3" className="accordion-collapse collapse" data-bs-parent="#servicesAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">Yes — commissioning support is part of what we offer, most often for owners pursuing LEED or code requirements on new construction and major renovations. It's a natural extension of our assessment work: the same team that knows how systems should perform can help verify they actually do. But our focus is assessment — diagnosing existing buildings and giving owners a clear, cost-saving path forward. Commissioning support complements that; it isn't the headline.</p>
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 mb-4 shadow-sm" data-aos="fade-up" data-aos-delay="300">
                  <h3 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq4">
                      Who do you typically work with on a project?
                    </button>
                  </h3>
                  <div id="faq4" className="accordion-collapse collapse" data-bs-parent="#servicesAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">We work directly for the owner, acting as their technical representative. On new construction, we coordinate with the design engineering team, general contractor, and mechanical/electrical subcontractors. On existing building assessments, we work alongside facility managers and their maintenance contractors. Our role is always to represent the owner's interests and ensure their requirements are met.</p>
                    </div>
                  </div>
                </div>
                
                <div className="accordion-item border-0 shadow-sm" data-aos="fade-up" data-aos-delay="400">
                  <h3 className="accordion-header">
                    <button className="accordion-button collapsed" type="button" data-bs-toggle="collapse" data-bs-target="#faq5">
                      What does an existing building assessment deliverable look like?
                    </button>
                  </h3>
                  <div id="faq5" className="accordion-collapse collapse" data-bs-parent="#servicesAccordion">
                    <div className="accordion-body">
                      <p className="mb-0">Our assessment reports document the current condition of each system and piece of equipment we inspect — including photographs, observations, and notes from discussions with maintenance staff. We provide a prioritized list of action items with recommendations for immediate repairs, near-term maintenance, and longer-term capital replacements. The goal is to give facility managers a clear, actionable picture of what their building needs.</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="section bg-primary text-white text-center">
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-lg-8">
              <h2 className="mb-4">Ready to discuss your project?</h2>
              <p className="lead mb-4">Whether you want an assessment of an existing facility, an energy and sustainability analysis, or commissioning support for a new project, we'd like to hear from you.</p>
              <Link to="/contact" className="btn btn-light btn-lg">
                Get in Touch
              </Link>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default Services;